import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, List, ListItem, TextField } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
// icons
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { grey } from "@mui/material/colors";
import { formatTimestampToWithoutTime, formatTimeStampToDate } from "utilities/common";
import { getConciliationDetails } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { openCustomModalDialog } from "../../../../redux/slices/modalSlice";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";

const RevenueConciliation = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { distributionId } = useParams()
  const [searchParams] = useSearchParams()
  const { driver, region, subRegion, forceClose, date } = Object.fromEntries(searchParams.entries())

  const [details, setDetails] = useState({});

  const handleChange = (e, reconciliationType) => {
    const { name, value } = e.target;
    const numValue = value?.replace(/[^0-9\-]/g, "");

    const newValues = { ...details, [reconciliationType]: { ...(details[reconciliationType] || {}), [name]: numValue } };

    if (newValues.cashReconciliation) {
      newValues.cashReconciliation.difference = (newValues?.cashReconciliation?.actualReturnedCash - newValues?.cashReconciliation?.expectedCash) || 0
    }
    if (newValues.creditCardReconciliation) {
      newValues.creditCardReconciliation.difference = (newValues?.creditCardReconciliation?.actualReturnedCredit - newValues?.creditCardReconciliation?.expectedCredit) || 0
    }

    setDetails(newValues);
  }

  const handleConciliationDetails = async () => {
    try {
      const request = { distributionId, driverId: driver, regionId: region, subRegionId: subRegion };
      const formattedDate = formatTimeStampToDate(Number(date));
      if (formattedDate !== "Invalid date") {
        request.date = formattedDate;
      }
      const response = await dispatch(getConciliationDetails(request)).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(data || {});
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  };

  useEffect(() => {
    if (distributionId && driver && region && subRegion) {
      handleConciliationDetails();
    }
  }, [distributionId, driver, region, subRegion])

  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Products List ({details?.date ? formatTimestampToWithoutTime(details?.date || "") : ""})
            </Typography>
          </Box>
        </Stack>

        {/* Top section */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Top details */}
            <Grid item xs={12} sm={6}>
              <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Revenue Reconciliation</Typography>
              <List sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Driver:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.driverName}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Region:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.region}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Sub Region:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.subRegion}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Kit ID:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.kitId}</Typography>
                </ListItem>
              </List>
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12} sm={6}>
              <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                <div className="d-flex align-item-top gap-2">
                  {
                    forceClose ? <MDButton
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: "240px" }}
                      onClick={() => {
                        dispatch(openCustomModalDialog({
                          modalData: { closureFormDetails: details },
                          modal: 'DRIVER_CLOSER_FORM'
                        }))
                      }}
                    >
                      Submit
                    </MDButton>
                      : <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "240px" }}
                        onClick={() => {
                          navigate(`/hyperwolf/closure-forms/final-report/${distributionId}?driver=${driver}&region=${region}&subRegion=${subRegion}&date=${date}`)
                        }}
                      >
                        Generate Final Report
                      </MDButton>
                  }
                </div>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        {/* Cash Reconciliation */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Stack direction='row' gap={3} xs={12} alignItems='start' justifyContent="space-between" flexDirection="column">
            <Card sx={{ p: { xs: 2 }, borderRadius: 2.5, boxShadow: "none", width: "100%", height: "100%", border: "1px solid #D0D0DA", flex: "1" }}>
              {/* Heading */}
              <Grid container alignItems="center" spacing={2} mb={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Cash Reconciliation</Typography>
                </Grid>

                {/* Details buttons */}
                <Grid item xs={12} sm={6}>
                  <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                    <div className="d-flex align-item-top gap-2">
                      {/* <MDButton
                        type="button"
                        variant="outlined"
                        color="dark"
                        sx={{ minWidth: "160px", fontSize: "14px" }}
                        onClick={() => {
                        }}
                      >
                        View Details
                      </MDButton> */}
                    </div>
                  </Stack>
                </Grid>
              </Grid>

              {/* Cash fields */}
              <Grid container spacing={3} mb={3}>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Expected Cash (Excluding Starting Cash)</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Expected Cash"
                    name="expectedCash"
                    disabled={!forceClose}
                    value={details?.cashReconciliation?.expectedCash ?? ""}
                    onChange={e => handleChange(e, "cashReconciliation")}
                  />
                </Grid>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Actual Returned Cash (counted)</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Actual Returned Cash"
                    name="actualReturnedCash"
                    disabled={!forceClose}
                    value={details?.cashReconciliation?.actualReturnedCash ?? ""}
                    onChange={e => handleChange(e, "cashReconciliation")}
                  />
                </Grid>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Difference</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Difference"
                    disabled
                    name="difference"
                    value={details?.cashReconciliation?.difference ?? ""}
                    onChange={e => handleChange(e, "cashReconciliation")}
                  />
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Stack direction='row' gap={1} alignItems='center' justifyContent="start">
                    <div className="d-flex align-item-top gap-2">
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "230px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                        onClick={() => {
                          dispatch(openCustomModalDialog({
                            modalData: {
                              distributionId,
                              closureType: "cash",
                              driverId: driver, region, subRegion,
                              handleConciliationDetails,
                              discrepancyId: details?.cashReconciliation?.discrepancyId,
                              note: details?.cashReconciliation?.discrepancyNote,
                              photos: details?.cashReconciliation?.discrepancyPhotos,
                              difference: details?.cashReconciliation?.difference,
                            },
                            modal: "ADD_CLOSURE_FORM_DISCREPANCY"
                          }));
                        }}
                      >
                        {details?.cashReconciliation?.discrepancyId ? "Change Discrepancy" : "+ Add Discrepancy"}
                      </MDButton>
                    </div>
                  </Stack>
                </Grid>
              </Grid>


              {/* Discrepancy details */}
              {details?.cashReconciliation?.discrepancyId && <Grid container alignItems="start" spacing={2} mt={1}>
                <Grid item xs={12}>
                  <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Discrepancy Information</Typography>
                </Grid>

                <Grid item xs={12}>
                  {/* Image */}
                  {details?.cashReconciliation?.discrepancyPhotos && details?.cashReconciliation?.discrepancyPhotos?.length > 0 && <Stack direction="row" flexWrap="wrap" mb={2} flexDirection="column">
                    <Typography variant='body2' color="#656575" mb={1}>Image</Typography>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {
                        details?.cashReconciliation?.discrepancyPhotos?.map((image, index) => (
                          <div style={{ position: "relative" }} key={index}>
                            <img
                              key={image}
                              src={image}
                              alt="Uploaded"
                              style={{
                                borderRadius: "20px",
                                width: "130px",
                                height: "130px",
                                objectFit: "cover",
                                boxShadow: "0 0 1px rgb(0 0 0 / 20%)",
                              }}
                            />
                          </div>
                        ))
                      }
                    </div>
                  </Stack>}

                  {/* Note */}
                  {details?.cashReconciliation?.discrepancyNote && <Box>
                    <Typography variant='body2' color="#656575">Note</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.cashReconciliation?.discrepancyNote}</Typography>
                  </Box>}
                </Grid>
              </Grid>}
            </Card>

            <Card sx={{ p: { xs: 2 }, borderRadius: 2.5, boxShadow: "none", width: "100%", height: "100%", border: "1px solid #D0D0DA", flex: "1" }}>
              {/* Heading */}
              <Grid container alignItems="center" spacing={2} mb={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Credit Card Reconciliation</Typography>
                </Grid>

                {/* Details buttons */}
                <Grid item xs={12} sm={6}>
                  <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                    <div className="d-flex align-item-top gap-2">
                      {/* <MDButton
                        type="button"
                        variant="outlined"
                        color="dark"
                        sx={{ minWidth: "160px", fontSize: "14px" }}
                        onClick={() => {
                        }}
                      >
                        View Details
                      </MDButton> */}
                    </div>
                  </Stack>
                </Grid>
              </Grid>

              {/* credit value fields */}
              <Grid container spacing={3} mb={3}>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Reported Credit Card (from)</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Expected Cash"
                    name="expectedCredit"
                    disabled={!forceClose}
                    value={details?.creditCardReconciliation?.expectedCredit ?? ""}
                    onChange={e => handleChange(e, "creditCardReconciliation")}
                  />
                </Grid>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Actual Returned Credit Card (counted)</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Actual Returned Credit Card"
                    disabled={!forceClose}
                    name="actualReturnedCredit"
                    value={details?.creditCardReconciliation?.actualReturnedCredit ?? ""}
                    onChange={e => handleChange(e, "creditCardReconciliation")}
                  />
                </Grid>
                <Grid item md={4}>
                  <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Difference</Typography>
                  <TextField
                    className="form-input"
                    fullWidth
                    placeholder="Difference"
                    disabled
                    name="difference"
                    value={details?.creditCardReconciliation?.difference ?? ""}
                    onChange={e => handleChange(e, "creditCardReconciliation")}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Stack direction='row' gap={1} alignItems='center' justifyContent="start">
                    <div className="d-flex align-item-top gap-2">
                      <MDButton
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "230px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                        onClick={() => {
                          dispatch(openCustomModalDialog({
                            modalData: {
                              distributionId,
                              closureType: "credit",
                              driverId: driver, region, subRegion,
                              handleConciliationDetails,
                              discrepancyId: details?.creditCardReconciliation?.discrepancyId,
                              note: details?.creditCardReconciliation?.discrepancyNote,
                              photos: details?.creditCardReconciliation?.discrepancyPhotos,
                              difference: details?.creditCardReconciliation?.difference,
                            },
                            modal: "ADD_CLOSURE_FORM_DISCREPANCY"
                          }));
                        }}
                      >
                        {details?.creditCardReconciliation?.discrepancyId ? "Change Discrepancy" : "+ Add Discrepancy"}
                      </MDButton>
                    </div>
                  </Stack>
                </Grid>
              </Grid>


              {/* Discrepancy details */}
              {details?.creditCardReconciliation?.discrepancyId && <Grid container alignItems="start" spacing={2} mt={1}>
                <Grid item xs={12}>
                  <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Discrepancy Information</Typography>
                </Grid>

                <Grid item xs={12}>
                  {/* Image */}
                  {details?.creditCardReconciliation?.discrepancyPhotos?.length > 0 && <Stack direction="row" flexWrap="wrap" mb={2} flexDirection="column">
                    <Typography variant='body2' color="#656575" mb={1}>Image</Typography>
                    {/* List uploaded photos */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                      {
                        details?.creditCardReconciliation?.discrepancyPhotos?.map((image, index) => (
                          <div style={{ position: "relative" }} key={index}>
                            <img
                              key={image}
                              src={image}
                              alt="Uploaded"
                              style={{
                                borderRadius: "20px",
                                width: "130px",
                                height: "130px",
                                objectFit: "cover",
                                boxShadow: "0 0 1px rgb(0 0 0 / 20%)",
                              }}
                            />
                          </div>
                        ))
                      }
                    </div>
                  </Stack>}

                  {/* Note */}
                  {details?.creditCardReconciliation?.discrepancyNote && <Box>
                    <Typography variant='body2' color="#656575">Note</Typography>
                    <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.creditCardReconciliation?.discrepancyNote}</Typography>
                  </Box>}
                </Grid>
              </Grid>}
            </Card>
          </Stack>
        </Card>
      </Box>
    </CommonLayout>
  )
}

export default RevenueConciliation;
